import { CalendarDays, ClipboardList, Info, MapPin, ReceiptText, User } from "lucide-react";
import React from "react";
import { FormattedNumber } from "react-intl";
import { Card, Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "randmarcomps";
import { useGetV4WarehousesQuery } from "randmar-api-client";
import { Requisition } from "../../stores/document/Document";
import CurrencyUtils from "../util/CurrencyUtils";
import FormatDateFromInt from "../util/DateTimeUtil";
import RequisitionPartNumberRow from "./RequisitionPartNumberRow";

interface IProps {
  requisition: Requisition;
}

type MetaItem = {
  id: string;
  label: string;
  value: React.ReactNode;
  helper?: string;
  Icon: React.ElementType;
};

const RequisitionView: React.FC<IProps> = ({ requisition }) => {
  const { data: warehouses } = useGetV4WarehousesQuery();

  const getWarehouseNameFromCode = React.useCallback((code?: string) => {
    if (!code || !warehouses) {
      return "B/O";
    }
    const warehouse = warehouses.find(w => w.WarehouseId === code);
    return warehouse?.Name || "B/O";
  }, [warehouses]);

  const sortedPartNumbers = React.useMemo(() => {
    return (requisition.partNumbers || [])
      .slice()
      .sort((a, b) => (b.mpn || "").localeCompare(a.mpn || ""));
  }, [requisition.partNumbers]);

  const lineItemCount = sortedPartNumbers.length;
  const extendedTotal = React.useMemo(
    () =>
      requisition.partNumbers
        ? requisition.partNumbers.reduce((total, part) => total + ((part.unitPrice || 0) * (part.quantityOrdered || 0)), 0)
        : 0,
    [requisition.partNumbers]
  );

  const metaItems = React.useMemo<MetaItem[]>(() => [
    {
      id: "draft-date",
      label: "Draft date",
      value: FormatDateFromInt(requisition.requisitionDate),
      helper: "When this draft was generated.",
      Icon: CalendarDays,
    },
    {
      id: "requested-by",
      label: "Requested by",
      value: requisition.requestedBy || "Unassigned",
      helper: "Owner of the draft purchase.",
      Icon: User,
    },
    {
      id: "reference",
      label: "Reference",
      value: requisition.reference && requisition.reference.trim() !== "" ? requisition.reference : "No reference added",
      helper: requisition.reference && requisition.reference.trim() !== "" ? undefined : "Add a note from the Actions menu.",
      Icon: Info,
    },
    {
      id: "location",
      label: "Location",
      value: getWarehouseNameFromCode(requisition.location),
      helper: "Fulfilled from this warehouse.",
      Icon: MapPin,
    },
    {
      id: "subtotal",
      label: "Subtotal",
      value: <FormattedNumber {...CurrencyUtils.format} value={requisition.subTotal || 0} />,
      helper: `${lineItemCount} line ${lineItemCount === 1 ? "item" : "items"}.`,
      Icon: ReceiptText,
    },
    {
      id: "total-cost",
      label: "Extended total",
      value: <FormattedNumber value={extendedTotal} {...CurrencyUtils.format} />,
      helper: "Quantity × unit price.",
      Icon: ClipboardList,
    },
  ], [extendedTotal, getWarehouseNameFromCode, lineItemCount, requisition.location, requisition.reference, requisition.requestedBy, requisition.requisitionDate, requisition.subTotal]);

  return (
    <section className="space-y-6" aria-label="Draft purchase details">
      <Card className="w-full rounded-2xl border border-gray-100 bg-white p-6 shadow-sm">
        <div className="space-y-4">
          <header className="space-y-2">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Draft purchase</p>
            <h2 className="text-[24px] font-semibold text-slate-900">Overview</h2>
            <p className="text-[15px] text-slate-600">
              Review quantities, costs, and context before converting this draft into a purchase order.
            </p>
          </header>

          <div className="flex flex-wrap items-center gap-2 text-sm text-slate-600" aria-live="polite">
            <span><span className="font-semibold text-slate-900">{lineItemCount}</span> line {lineItemCount === 1 ? "item" : "items"}</span>
            <span aria-hidden="true">•</span>
            <span>Location: <span className="font-semibold text-slate-900">{getWarehouseNameFromCode(requisition.location)}</span></span>
            <span aria-hidden="true">•</span>
            <span>Subtotal: <span className="font-semibold text-slate-900"><FormattedNumber {...CurrencyUtils.format} value={requisition.subTotal || 0} /></span></span>
          </div>

          <dl className="grid gap-3 md:grid-cols-2 lg:grid-cols-3" aria-label="Draft purchase metadata">
            {metaItems.map(({ id, label, value, helper, Icon }) => (
              <div key={id} className="rounded-2xl border border-gray-100 bg-white p-5 shadow-sm">
                <div className="flex items-start gap-4">
                  <span className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-[var(--ops-accent-muted)] text-[var(--ops-accent)]">
                    <Icon className="h-6 w-6" aria-hidden="true" />
                  </span>
                  <div className="space-y-1">
                    <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</dt>
                    <dd className="text-[15px] font-medium text-slate-900">{value}</dd>
                    {helper ? <p className="text-[13px] text-slate-500">{helper}</p> : null}
                  </div>
                </div>
              </div>
            ))}
          </dl>
        </div>
      </Card>

      <Card className="w-full rounded-2xl border border-gray-100 bg-white p-6 shadow-sm" aria-labelledby="requisition-partnumbers-title">
        <div className="flex flex-col gap-3">
          <header className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Line items</p>
              <h3 id="requisition-partnumbers-title" className="text-[20px] font-medium text-slate-900">
                Part numbers ({lineItemCount})
              </h3>
            </div>
            <div className="text-sm text-slate-500">
              {requisition.reference && requisition.reference.trim() !== ""
                ? <>Reference: <span className="font-semibold text-slate-800">{requisition.reference}</span></>
                : "No reference provided"}
            </div>
          </header>

          <div className="rounded-2xl border border-gray-100 bg-gray-50 p-4 text-[15px] text-slate-700" role="note" aria-label="Editing guidance">
            <div className="flex items-start gap-3">
              <Info className="mt-0.5 h-5 w-5 text-slate-400" aria-hidden="true" />
              <div>
                <p className="font-medium text-slate-900">Adjust quantities in-line.</p>
                <p className="text-[13px] text-slate-600">Press Enter after editing a quantity to confirm faster.</p>
              </div>
            </div>
          </div>

          <div className="overflow-x-auto rounded-2xl border border-gray-100">
            <Table>
              <TableHeader className="bg-gray-50">
                <TableRow className="h-14 hover:bg-gray-50">
                  <TableHead className="min-w-[240px] text-xs font-semibold uppercase tracking-wide text-slate-500">Product</TableHead>
                  <TableHead className="w-[140px] text-center text-xs font-semibold uppercase tracking-wide text-slate-500">Quantity</TableHead>
                  <TableHead className="w-[140px] text-center text-xs font-semibold uppercase tracking-wide text-slate-500">Update</TableHead>
                  <TableHead className="w-[160px] text-center text-xs font-semibold uppercase tracking-wide text-slate-500">Unit price</TableHead>
                  <TableHead className="w-[160px] text-center text-xs font-semibold uppercase tracking-wide text-slate-500">Extended</TableHead>
                  <TableHead className="w-[120px] text-center text-xs font-semibold uppercase tracking-wide text-slate-500">Details</TableHead>
                  <TableHead className="w-[120px] text-center text-xs font-semibold uppercase tracking-wide text-slate-500">Remove</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedPartNumbers.length === 0 ? (
                  <TableRow className="h-14">
                    <TableCell colSpan={7} className="text-center text-sm text-slate-500">
                      This draft purchase has no part numbers yet.
                    </TableCell>
                  </TableRow>
                ) : (
                  sortedPartNumbers.map((row, index) => (
                    <RequisitionPartNumberRow
                      key={`RequisitionPartNumber-${row.mpn ?? row.randmarSKU ?? "unknown"}-${index}`}
                      index={index}
                      partNumber={row}
                      requisitionNumber={requisition.requisitionNumber || ""}
                    />
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </div>
      </Card>
    </section>
  );
};

export default RequisitionView;
